import React, { useState, useEffect } from "react";
import api from '../../utils/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

export default function AddModal({ open, handleClose, handleSnack, onSuccess }) {
  const [societes, setSocietes] = useState([]);
  
  useEffect(() => {
    if (open) {
      fetchSocietes();
    }
  }, [open]);
  
  const fetchSocietes = async () => {
    try {
      const response = await api.get('/societes');
      setSocietes(response.data || []);
    } catch (error) {
      console.error('Error fetching societes:', error);
      handleSnack('Erreur lors du chargement des sociétés', 'error');
    }
  };
  const [formData, setFormData] = useState({
    societe: "",
    nom: "",
    latitude: "",
    longitude: "",
    statut: "Active",
  });
  
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  
  const validateForm = () => {
    const newErrors = {};
    // Only name is required, other fields can be nullable
    if (!formData.nom) newErrors.nom = "Nom est requis";
    
    // Validate numeric fields if they are provided
    if (formData.latitude && isNaN(Number(formData.latitude))) {
      newErrors.latitude = "La latitude doit être un nombre";
    }
    if (formData.longitude && isNaN(Number(formData.longitude))) {
      newErrors.longitude = "La longitude doit être un nombre";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const resetForm = () => {
    setFormData({
      societe: "",
      nom: "",
      latitude: "",
      longitude: "",
      statut: "Active",
    });
    setErrors({});
    setGeneralError("");
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");
    if (validateForm()) {
      try {
        const siteData = {
          name: formData.nom,
          ID_Societe: formData.societe,
          latitude: formData.latitude,
          longitude: formData.longitude,
          Status: formData.statut
        };
        
        await api.post('/sites', siteData);
        handleSnack('Site ajouté avec succès', 'success');
        handleClose();
        resetForm();
        // Refresh the sites list in parent component
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      } catch (error) {
        console.error("Erreur lors de l'ajout du site:", error);
        
        // Handle validation errors (422)
        if (error.response && error.response.status === 422) {
          const serverErrors = error.response.data.errors;
          const newErrors = {};
          
          // Map backend field names to frontend field names
          if (serverErrors.name) newErrors.nom = serverErrors.name[0];
          if (serverErrors.ID_Societe) newErrors.societe = serverErrors.ID_Societe[0];
          if (serverErrors.latitude) newErrors.latitude = serverErrors.latitude[0];
          if (serverErrors.longitude) newErrors.longitude = serverErrors.longitude[0];
          if (serverErrors.Status) newErrors.statut = serverErrors.Status[0];
          
          setErrors(newErrors);
          
          // If there's a general error message
          if (error.response.data.message) {
            setGeneralError(error.response.data.message);
          }
        } 
        // Handle validation errors (400)
        else if (error.response && error.response.status === 400) {
          const serverErrors = error.response.data;
          const newErrors = {};
          
          // Map backend field names to frontend field names
          if (serverErrors.name) newErrors.nom = serverErrors.name[0];
          if (serverErrors.ID_Societe) newErrors.societe = serverErrors.ID_Societe[0];
          if (serverErrors.latitude) newErrors.latitude = serverErrors.latitude[0];
          if (serverErrors.longitude) newErrors.longitude = serverErrors.longitude[0];
          if (serverErrors.Status) newErrors.statut = serverErrors.Status[0];
          
          setErrors(newErrors);
        }
        // Handle server errors (500)
        else if (error.response && error.response.status === 500) {
          setGeneralError(error.response.data.message || "Une erreur s'est produite lors du traitement de votre demande");
        }
        // Handle network errors or other issues
        else {
          setGeneralError("Erreur de connexion au serveur. Veuillez réessayer plus tard.");
        }
        
        handleSnack("Erreur lors de l'ajout du site", 'error');
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      height: 35,
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: 'red' }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mr: 3 }}>
        AJOUTER UN SITE
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        <Typography variant="subtitle2">Société</Typography>
        <FormControl fullWidth sx={inputStyle} error={!!errors.societe}>
          <Select name="societe" value={formData.societe} onChange={handleChange}>
            <MenuItem value="">Sélectionner une société</MenuItem>
            {societes.map(societe => (
              <MenuItem key={societe.ID_Societe} value={societe.ID_Societe}>
                {societe.name}
              </MenuItem>
            ))}
          </Select>
          {errors.societe && <FormHelperText error>{errors.societe}</FormHelperText>}
        </FormControl>
        <Typography variant="subtitle2">Nom</Typography>
        <TextField 
          fullWidth 
          name="nom" 
          value={formData.nom} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.nom}
          helperText={errors.nom}
        />
        <Typography variant="subtitle2">Latitude</Typography>
        <TextField 
          fullWidth 
          name="latitude" 
          value={formData.latitude} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.latitude}
          helperText={errors.latitude}
        />
        <Typography variant="subtitle2">Longitude</Typography>
        <TextField 
          fullWidth 
          name="longitude" 
          value={formData.longitude} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.longitude}
          helperText={errors.longitude}
        />
        <Typography variant="subtitle2">Statut</Typography>
        <FormControl error={!!errors.statut}>
          <RadioGroup row name="statut" value={formData.statut} onChange={handleChange}>
            <FormControlLabel value="Active" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Active" />
            <FormControlLabel value="Inactive" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Inactive" />
          </RadioGroup>
          {errors.statut && <FormHelperText error>{errors.statut}</FormHelperText>}
        </FormControl>
      </DialogContent>
      <Divider sx={{ mb: 1 }} />
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} startIcon={<CloseIcon />} onClick={handleClose}>
          Close
        </Button>
        <Button variant="outlined" sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} startIcon={<CheckIcon />} onClick={handleSubmit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
